import { getPlatformDisplayName } from 'src/utils/platform-label';

// What one connected account is known by on its own platform: the seller's username, their shop,
// whatever the platform gave us. Empty when the row carries none.
function accountIdentifier(marketplace) {
  return marketplace?.accountIdentifier || marketplace?.accountKey || '';
}

// Builds the display label for one connected marketplace row, e.g. 'eBay (seller_us)'.
// Always shows the account identifier so the user can tell accounts on the same
// platform apart, unless the store's own label already spells it out.
export function marketplaceLabel(marketplace) {
  if (!marketplace) return '';
  const baseLabel = marketplace.label || marketplace.platform || '';
  const identifier = accountIdentifier(marketplace);
  if (identifier && !baseLabel.toLowerCase().includes(identifier.toLowerCase())) {
    return `${baseLabel} (${identifier})`;
  }
  return baseLabel;
}

// Just the part that tells one account apart from the others on the same marketplace. For a card
// shown under a heading that already names the marketplace, repeating that name says nothing —
// which shop it is, is the whole point. Falls back to the row's own label for an account the
// platform gave no identifier for.
export function accountOnlyLabel(marketplace) {
  return accountIdentifier(marketplace) || marketplaceLabel(marketplace);
}

// Resolves the label for a specific platform + account from the connected
// marketplaces list, e.g. for showing "which eBay shop" a result came from.
// Falls back to the platform's plain display name when that account is no
// longer in the list (disconnected, or never loaded).
export function accountLabel(marketplaces, platform, integrationId) {
  const row = (marketplaces || []).find((mp) => mp.integrationId === integrationId);
  if (!row) return getPlatformDisplayName(platform);
  return marketplaceLabel(row);
}

// The seller's own name for one account, when saying it tells them something. A seller who never
// named their BigCommerce store has it called "BigCommerce", and "the same BigCommerce account
// (BigCommerce)" says the marketplace twice and the account not at all — so that name is dropped.
export function distinctAccountName(platform, label) {
  if (!label) return '';
  return label.toLowerCase() === String(getPlatformDisplayName(platform)).toLowerCase() ? '' : label;
}

// What to call one account on screen: the marketplace, and which account it is when the seller
// named it something of their own — 'eBay (Main Store)', or just 'eBay'.
//
// A name that already opens with the marketplace ('eBay (US)') names it for us, so it is shown as
// it is: wrapping it again would read 'eBay (eBay (US))'.
export function accountDisplayName(platform, label) {
  const name = distinctAccountName(platform, label);
  const platformName = getPlatformDisplayName(platform);

  if (!name) return platformName;
  if (name.toLowerCase().startsWith(String(platformName).toLowerCase())) return name;

  return `${platformName} (${name})`;
}
